import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Switch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PAGE_URLS } from '../constants';
import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';
import Loading from '../components/Loader';
import Login from '../pages/Login';
import Dashboard from '../pages/Dashboard';
import Clients from '../pages/Clients';
import Project from '../pages/Project';
import ArchivedProject from '../pages/ArchivedProject';
import Team from '../pages/Team';
import TeamGroups from '../pages/TeamGroups';
import Inactiveteam from '../pages/InactiveTeam';
import DetailedTimesheet from '../pages/Timesheet';
import SummarizedTimesheet from '../pages/SummarizedTimesheet';
import TimesheetView from '../pages/TimesheetView';
import TimesheetApproval from '../pages/TimesheetApproval';
import WorksessionEditRequests from '../pages/WorksessionEditRequests';
import MemberActivity from '../pages/MemberActivity';
import TeamDateRangeActivity from '../pages/TeamDateRangeActivity';
import ScreenshotReport from '../pages/ScreenshotReport';
import AdherenceReport from '../pages/AdherenceReport';
import Charts from '../pages/Charts';
import Profile from '../pages/account_settings/Profile';
import ChangePassword from '../pages/account_settings/ChangePassword';
import TeamSettings from '../pages/account_settings/Teams';
import RolesAndPermissions from '../pages/account_settings/RolesAndPermissions';
import FeatureSettings from '../pages/account_settings/FeatureSettings';
import Billing from '../pages/account_settings/Billing';
import EmailSummaries from '../pages/account_settings/EmailSummaries';
import OvertimeWeeklyReport from '../pages/features/overtime/Report';
// Tracking
import Tracking from '../pages/Tracking';
import CountryAndSites from '../pages/CountryAndSite';
import BreakManagement from '../pages/BreakManagement';
import OvertimeSlots from '../pages/OvertimeSlots';
import OvertimeCommitment from '../pages/OvertimeCommitment';

const ForgotPassword = lazy(() => import('../pages/user/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/user/ResetPassword'));
const MemberRegistration = lazy(() =>
  import('../pages/user/MemberRegistration')
);
const FreeTrialRegistration = lazy(() =>
  import('../pages/user/FreetrialRegistration')
);

const FreeTrialRegistrationNew = lazy(() =>
  import('../pages/user/FreetrialRegistrationNew')
);

// Team selection - Multi team user
const TeamSelection = lazy(() => import('../pages/TeamSelection'));

// Projects
const CreateProject = lazy(() => import('../pages/CreateProject'));
const ProjectOverview = lazy(() => import('../pages/ProjectOverview'));
const ProjectTasks = lazy(() => import('../pages/ProjectTasks'));
const ProjectTasksBoard = lazy(() => import('../pages/ProjectTasksBoard'));
const ProjectTimeline = lazy(() => import('../pages/ProjectTimeline'));
const OnboardSurvey = lazy(() => import('../pages/OnboardSurvey'));
const ProjectSettings = lazy(() => import('../pages/ProjectSettings'));
const ProjectTeamMembers = lazy(() => import('../pages/ProjectTeamMembers'));
const ProjectWorkSessions = lazy(() => import('../pages/ProjectWorkSessions'));
const ProjectEditWorkSession = lazy(() =>
  import('../pages/ProjectEditWorkSession')
);

// Team
const Invite = lazy(() => import('../pages/Invite'));
const MemberProjects = lazy(() => import('../pages/MemberProjects'));
const MemberEditWorkSession = lazy(() =>
  import('../pages/MemberEditWorkSession')
);

// Scheduling
const Scheduling = lazy(() => import('../pages/Scheduling'));
const Attendance = lazy(() => import('../pages/features/attendance/Report'));
const Logs = lazy(() => import('../pages/Logs'));
const ScheduledActivity = lazy(() => import('../pages/ScheduledActivity'));
const ShrinkageReport = lazy(() => import('../pages/ShrinkageReport'));
const Holidays = lazy(() => import('../pages/Holidays'));
const Leaves = lazy(() => import('../pages/LeaveManagement'));
const PolicyMangement = lazy(() => import('../pages/PolicyManagement'));
const TimeOffTypes = lazy(() => import('../pages/TimeOffTypes'));
const ScheduleTypes = lazy(() => import('../pages/ScheduleTypes'));
const ScheduleChangeRequests = lazy(() =>
  import('../pages/ScheduleChangeRequests')
);

// Timeline
const Timeline = lazy(() => import('../pages/Timeline'));

// Activity
const TeamDayActivity = lazy(() => import('../pages/TeamDayActivity'));
const OngoingSessions = lazy(() => import('../pages/OngoingSessions'));
// const UrlActivityTracking = lazy(() => import('../pages/UrlActiivtyTracking'));
// const AppActivityTracking = lazy(() => import('../pages/AppActiivtyTracking'));

// Reports
const ViewScreenshots = lazy(() => import('../pages/ViewScreenshots'));

// Settings
const CardUpdateReturn = lazy(() =>
  import('../pages/account_settings/CardUpdateReturn')
);
const AccountClosed = lazy(() =>
  import('../pages/account_settings/AccountClosed')
);
const TrialUpgradeReturn = lazy(() =>
  import('../pages/account_settings/FreeTrialUpgradeReturn')
);
const ReactivateReturn = lazy(() =>
  import('../pages/account_settings/ReactivateReturn')
);
const AccountReactivated = lazy(() =>
  import('../pages/account_settings/AccountReactivated')
);

// Asana Integration
const AsanaReturn = lazy(() => import('../pages/integrations/AsanaReturn'));
const IntegrationList = lazy(() =>
  import('../pages/integrations/IntegrationList')
);
const IntegrationStatus = lazy(() =>
  import('../pages/integrations/IntegratedFiles')
);

// Zapier Integration
const ZapierReturn = lazy(() =>
  import('../pages/integrations/ZapierReturnUrl')
);

// Invoice
const Invoice = lazy(() => import('../pages/features/invoice/List'));
const EditInvoice = lazy(() => import('../pages/features/invoice/Edit'));
const ViewInvoice = lazy(() => import('../pages/features/invoice/View'));

// Change log
const Changelog = lazy(() => import('../pages/Changelog'));

// SSO Callback
const SSOCallback = lazy(() => import('../pages/SSOCallback'));

// Captcha
const Captcha = lazy(() => import('../pages/captcha/Captcha'));

// Not Found
const NotFound = lazy(() => import('../pages/NotFound'));
// Helper document
const PermissionDocument = lazy(() =>
  import('../pages/HelpDocumentPermissions')
);

const Routes = () => {
  const billing = useSelector((state) => state.billingReducer);
  const permissions = useSelector((state) => state.permissionsReducer);
  return (
    <Router>
      <Suspense fallback={<Loading />}>
        <Switch>
          <PublicRoute exact path="/login" component={Login} />
          <PublicRoute exact path="/captcha" component={Captcha} />
          <PublicRoute exact path="/sso/callback" component={SSOCallback} />
          <PublicRoute exact path="/login/invite" component={Invite} />
          <PublicRoute exact path="/register" component={MemberRegistration} />
          <PublicRoute exact path="/user/confirm/:cnfCode" component={Login} />
          <PublicRoute exact path="/weblogin" component={Login} />
          <PublicRoute exact path="/zapier-return" component={ZapierReturn} />
          <PublicRoute
            exact
            path="/freetrial"
            component={FreeTrialRegistration}
          />
          <PublicRoute
            exact
            path="/freetrial-new"
            component={FreeTrialRegistrationNew}
          />
          <PublicRoute exact path="/error" component={NotFound} />
          <PrivateRoute
            exact
            path="/team-selection"
            component={TeamSelection}
          />
          <PrivateRoute
            exact
            path="/help/permissions"
            component={PermissionDocument}
          />
          {permissions && permissions.includes('dashboard_view') && (
            <PrivateRoute
              exact
              path={PAGE_URLS.DASHBOARD}
              component={Dashboard}
            />
          )}
          {permissions && permissions.includes('client_manage') && (
            <PrivateRoute exact path={PAGE_URLS.CLIENTS} component={Clients} />
          )}
          {permissions && permissions.includes('custom_role_manage') && (
            <PrivateRoute
              exact
              path={`/manage${PAGE_URLS.ROLES_AND_PERMISSIONS}`}
              component={RolesAndPermissions}
            />
          )}
          <PrivateRoute exact path="/changelog" component={Changelog} />
          <PrivateRoute exact path={PAGE_URLS.TEAM.ACTIVE} component={Team} />
          <PrivateRoute
            exact
            path={PAGE_URLS.TEAM.INACTIVE}
            component={Inactiveteam}
          />
          {permissions && permissions.includes('schedule_import') && (
            <PrivateRoute
              exact
              path="/reports/overtime-commitments"
              component={OvertimeCommitment}
            />
          )}
          <PrivateRoute
            exact
            path={PAGE_URLS.TEAM.TEAM_GROUPS}
            component={TeamGroups}
          />
          <PrivateRoute
            exact
            path="/team/projects/:id"
            component={MemberProjects}
          />
          {permissions && permissions.includes('work_session_edit') && (
            <PrivateRoute
              exact
              path="/team/worksession/:id"
              component={MemberEditWorkSession}
            />
          )}
          <PrivateRoute
            exact
            path={PAGE_URLS.PROJECTS.ACTIVE}
            component={Project}
          />
          <PrivateRoute
            exact
            path="/projects/archived"
            component={ArchivedProject}
          />
          {permissions && permissions.includes('project_create') && (
            <PrivateRoute
              exact
              path="/projects/create"
              component={CreateProject}
            />
          )}
          <PrivateRoute
            exact
            path="/projects/:id/overview"
            component={ProjectOverview}
          />{' '}
          <PrivateRoute
            exact
            path="/projects/:id/tasks"
            component={ProjectTasks}
          />
          <PrivateRoute exact path="/onboarding" component={OnboardSurvey} />
          <PrivateRoute
            exact
            path="/projects/:id/task-list"
            component={ProjectTasks}
          />
          <PrivateRoute
            exact
            path="/projects/:id/task-board"
            component={ProjectTasksBoard}
          />
          <PrivateRoute
            exact
            path="/projects/:id/timeline"
            component={ProjectTimeline}
          />
          <PrivateRoute
            exact
            path="/projects/:id/team-members"
            component={ProjectTeamMembers}
          />
          <PrivateRoute
            exact
            path="/projects/:id/worksessions"
            component={ProjectWorkSessions}
          />
          <PrivateRoute
            exact
            path="/projects/:id/edit-worksession"
            component={ProjectEditWorkSession}
          />
          <PrivateRoute
            exact
            path="/projects/:id/settings"
            component={ProjectSettings}
          />
          <PrivateRoute
            exact
            path="/projects/:id/settings"
            component={ProjectSettings}
          />
          <PrivateRoute
            exact
            path="/timesheet/detailed"
            component={DetailedTimesheet}
          />
          {permissions && permissions.includes('timesheet_summarized_view') && (
            <PrivateRoute
              exact
              path="/timesheet/summarized"
              component={SummarizedTimesheet}
            />
          )}
          <PrivateRoute
            exact
            path="/timesheet/view"
            component={TimesheetView}
          />
          {permissions && permissions.includes('timesheet_approval') && (
            <PrivateRoute
              exact
              path="/timesheet/approval"
              component={TimesheetApproval}
            />
          )}
          {permissions &&
            permissions.includes('work_session_request_approval') && (
              <PrivateRoute
                exact
                path="/timesheet/change-requests"
                component={WorksessionEditRequests}
              />
            )}
          <PrivateRoute
            exact
            path={PAGE_URLS.ACTIVITY.MEMBER}
            component={MemberActivity}
          />
          {/* URL & App Tracking is disabled for now because this feature is not ready on Desktop client */}
          {/* {permissions && permissions.includes('url_and_app_tracking') && (
            <PrivateRoute
              exact
              path={PAGE_URLS.ACTIVITY.URL_TRACKING}
              component={UrlActivityTracking}
            />
          )} */}
          {/* {permissions && permissions.includes('url_and_app_tracking') && (
            <PrivateRoute
              exact
              path={PAGE_URLS.ACTIVITY.APP_TRACKING}
              component={AppActivityTracking}
            />
          )} */}
          {permissions && permissions.includes('activity_team_view') && (
            <PrivateRoute exact path={PAGE_URLS.LOGS} component={Logs} />
          )}
          {permissions && permissions.includes('schedule_activity_report') && (
            <PrivateRoute
              exact
              path={PAGE_URLS.SCHEDULED_ACTIVITY.MEMBER}
              component={ScheduledActivity}
            />
          )}
          <PrivateRoute
            exact
            path="/activity/worksession"
            component={ViewScreenshots}
          />
          {permissions && permissions.includes('activity_team_view') && (
            <PrivateRoute
              exact
              path={PAGE_URLS.ACTIVITY.TEAM}
              component={TeamDateRangeActivity}
            />
          )}
          {permissions && permissions.includes('work_session_ongoing_view') && (
            <PrivateRoute
              exact
              path={PAGE_URLS.ACTIVITY.ONGOING_SESSIONS}
              component={OngoingSessions}
            />
          )}
          <PrivateRoute exact path="/schedules/shifts" component={Scheduling} />
          {permissions && permissions.includes('schedule_import') && (
            <PrivateRoute
              exact
              path="/schedules/overtime-slots"
              component={OvertimeSlots}
            />
          )}
          <PrivateRoute exact path="/timeline" component={Timeline} />
          {permissions && permissions.includes('track_page_view') && (
            <PrivateRoute exact path="/track" component={Tracking} />
          )}
          <PrivateRoute exact path="/timeoff/requests" component={Leaves} />
          {permissions && permissions.includes('time_off_settings') && (
            <PrivateRoute
              exact
              path="/timeoff/policy"
              component={PolicyMangement}
            />
          )}
          {permissions && permissions.includes('time_off_settings') && (
            <PrivateRoute
              exact
              path="/timeoff/types"
              component={TimeOffTypes}
            />
          )}
          {permissions && permissions.includes('schedule_type_manage') && (
            <PrivateRoute
              exact
              path="/schedules/types"
              component={ScheduleTypes}
            />
          )}
          <PrivateRoute exact path="/schedules/holidays" component={Holidays} />
          <PrivateRoute
            exact
            path="/schedules/change-requests"
            component={ScheduleChangeRequests}
          />
          {permissions && permissions.includes('activity_team_view') && (
            <PrivateRoute
              exact
              path="/activity/team/day"
              component={TeamDayActivity}
            />
          )}
          {permissions && permissions.includes('activity_team_view') && (
            <PrivateRoute
              exact
              path="/activity/team/day/:date"
              component={TeamDayActivity}
            />
          )}
          <PrivateRoute
            exact
            path="/settings/password"
            component={ChangePassword}
          />
          <PrivateRoute exact path="/settings/profile" component={Profile} />
          <PrivateRoute exact path="/settings/teams" component={TeamSettings} />
          {permissions && permissions.includes('custom_role_manage') && (
            <PrivateRoute
              exact
              path="/settings/roles-and-permissions"
              component={RolesAndPermissions}
            />
          )}
          {permissions && permissions.includes('country_sites_manage') && (
            <PrivateRoute
              exact
              path={PAGE_URLS.COUNTRY_AND_SITES}
              component={CountryAndSites}
            />
          )}
          {permissions && permissions.includes('break_manage') && (
            <PrivateRoute
              exact
              path={PAGE_URLS.BREAKS}
              component={BreakManagement}
            />
          )}
          {permissions && permissions.includes('configuration_manage') && (
            <PrivateRoute
              exact
              path="/settings/general"
              component={FeatureSettings}
            />
          )}
          <PrivateRoute
            exact
            path="/reports/overtime"
            component={OvertimeWeeklyReport}
          />
          <PrivateRoute
            exact
            path="/reports/attendance"
            component={Attendance}
          />
          {permissions &&
            (permissions.includes('adherence_report') ||
              permissions.includes('shrinkage_view') ||
              permissions.includes('tardy_approval') ||
              permissions.includes('tardy_request')) && (
              <PrivateRoute
                exact
                path="/reports/adherence-report"
                component={AdherenceReport}
              />
            )}
          {permissions && permissions.includes('shrinkage_view') && (
            <PrivateRoute
              exact
              path="/reports/shrinkage"
              component={ShrinkageReport}
            />
          )}
          <PrivateRoute
            exact
            path="/reports/screenshot"
            component={ScreenshotReport}
          />
          <PrivateRoute exact path="/reports/charts" component={Charts} />
          {permissions && permissions.includes('billing_invoice_view') && (
            <PrivateRoute exact path="/settings/billing" component={Billing} />
          )}
          <PublicRoute exact path="/login" component={Login} />
          <PublicRoute
            exact
            path="/forgot-password"
            component={ForgotPassword}
          />
          <PublicRoute exact path="/reset-password" component={ResetPassword} />
          <PrivateRoute
            exact
            path="/settings/card-update-return"
            component={CardUpdateReturn}
          />
          <PrivateRoute
            exact
            path="/settings/trial-upgrade-return"
            component={TrialUpgradeReturn}
          />
          <PrivateRoute exact path="/asana-return" component={AsanaReturn} />
          <PrivateRoute
            exact
            path="/settings/integration-status"
            component={IntegrationStatus}
          />
          <PrivateRoute
            exact
            path="/settings/integration-list"
            component={IntegrationList}
          />
          {permissions &&
            permissions.includes('subscription_manage') &&
            billing.accountStatus === 'cancel' && (
              <PrivateRoute
                exact
                path="/settings/account-closed"
                component={AccountClosed}
              />
            )}
          {permissions &&
            permissions.includes('subscription_manage') &&
            billing.accountStatus === 'reactivated' && (
              <PrivateRoute
                exact
                path="/settings/account-reactivated"
                component={AccountReactivated}
              />
            )}
          <PublicRoute
            exact
            path="/settings/reactivate-return"
            component={ReactivateReturn}
          />
          <PublicRoute exact path="/zapier-return" component={ZapierReturn} />
          {permissions && permissions.includes('email_summaries_manage') && (
            <PrivateRoute
              exact
              path="/settings/email-summaries"
              component={EmailSummaries}
            />
          )}
          {permissions && permissions.includes('invoice_manage') && (
            <PrivateRoute exact path={PAGE_URLS.INVOICES} component={Invoice} />
          )}
          {permissions && permissions.includes('invoice_manage') && (
            <PrivateRoute
              exact
              path="/invoice/edit/:id"
              component={EditInvoice}
            />
          )}
          {permissions && permissions.includes('invoice_manage') && (
            <PrivateRoute
              exact
              path="/invoice/view/:id"
              component={ViewInvoice}
            />
          )}
          <PrivateRoute component={NotFound} />
        </Switch>
      </Suspense>
    </Router>
  );
};
export default Routes;
